import { defaultReadingPreferences, type LocatorJson, type ReadingPreferences } from "@msb/reader-bridge";
import { parseAnnotations, sortAnnotations, type Annotation } from "./annotations.ts";
import { findBookmark, samePage } from "./bookmarks.ts";
import type { SyncStore } from "./sync.ts";
import type { Bookmark } from "./storage.ts";

/*
 * Ce que le lecteur synchronise, dans le format commun avec l'app (la position d'un livre a pour id l'identifiant
 * produit Shopware, les réglages de lecture l'id « reading »). Sans synchro (extrait, hors connexion), ces
 * fonctions rendent la copie locale telle quelle.
 */

export const PREFERENCES_ID = "reading";

/**
 * La position à rouvrir : celle de la synchro, la plus récente des appareils ; sinon la copie locale, qu'on envoie
 * aussitôt pour que les autres appareils la trouvent.
 */
export function locationToOpen(sync: SyncStore | undefined, productId: string | undefined, local: LocatorJson | undefined): LocatorJson | undefined {
  if (!sync || !productId) return local;
  const synced = sync.get("location", productId)?.data as LocatorJson | undefined;
  if (synced?.href) return synced;
  if (local) sync.put("location", productId, productId, local as unknown as Record<string, unknown>);
  return local;
}

/** Ce qu'un appareil qui a déjà noté la position ne doit pas écraser sans le dire : la marge qui absorbe l'écart d'horloge. */
const SAME_DEVICE_MARGIN_MS = 2000;

/** Écart d'avancement (2 % du livre) en dessous duquel deux appareils sont au même endroit. */
const SAME_PLACE_MARGIN = 0.02;

/**
 * Au même endroit, d'un appareil à l'autre : la position Readium de l'app et celle du lecteur web ne se comptent pas
 * pareil, on compare donc l'avancement dans le livre quand les deux le donnent.
 */
function samePlace(a: LocatorJson, b: LocatorJson): boolean {
  const ta = a.locations?.totalProgression;
  const tb = b.locations?.totalProgression;
  if (typeof ta === "number" && typeof tb === "number") return Math.abs(ta - tb) < SAME_PLACE_MARGIN;
  return samePage(a, b);
}

export interface OpeningPosition {
  open: LocatorJson | undefined;
  /** La position d'un autre appareil, plus récente et ailleurs : on la propose, on n'y saute pas. */
  elsewhere?: LocatorJson;
}

/**
 * Où ouvrir le livre. La position de cet appareil reste la position d'ouverture ; si un autre appareil a lu plus
 * récemment et ailleurs, on la signale pour que le lecteur choisisse. Sans position locale, celle du compte.
 */
export function positionToOpen(
  sync: SyncStore | undefined,
  productId: string | undefined,
  local: LocatorJson | undefined,
  localTime: number,
): OpeningPosition {
  if (!sync || !productId) return { open: local };
  const entry = sync.get("location", productId);
  const synced = entry?.data as LocatorJson | undefined;
  if (!entry || !synced?.href) {
    if (local) sync.put("location", productId, productId, local as unknown as Record<string, unknown>);
    return { open: local };
  }
  if (!local) return { open: synced };
  if (samePlace(synced, local)) return { open: local };
  if (Date.parse(entry.updated_at) > localTime + SAME_DEVICE_MARGIN_MS) return { open: local, elsewhere: synced };
  return { open: local };
}

export function recordLocation(sync: SyncStore | undefined, productId: string | undefined, locator: LocatorJson): void {
  if (sync && productId) sync.put("location", productId, productId, locator as unknown as Record<string, unknown>);
}

const asBookmark = (item: { id: string; data: Record<string, unknown> }): Bookmark | undefined => {
  const locator = item.data.locator as LocatorJson | undefined;
  if (!locator?.href) return undefined;
  return { id: item.id, locator, createdAt: typeof item.data.createdAt === "string" ? item.data.createdAt : new Date(0).toISOString() };
};

/** Les signets du livre : ceux de la synchro, plus les signets locaux jamais envoyés (envoyés à cette occasion). */
export function bookmarksToOpen(sync: SyncStore | undefined, productId: string | undefined, local: Bookmark[]): Bookmark[] {
  if (!sync || !productId) return local;
  const synced = sync.list("bookmark", productId).map(asBookmark).filter((b): b is Bookmark => !!b);
  const known = new Set(synced.map((b) => b.id));
  for (const bookmark of local) {
    if (known.has(bookmark.id) || sync.has("bookmark", bookmark.id)) continue;
    sync.put("bookmark", bookmark.id, productId, { locator: bookmark.locator, createdAt: bookmark.createdAt });
    synced.push(bookmark);
  }
  // Deux appareils qui marquent la même page donnent deux signets : on n'en montre qu'un, le plus ancien.
  const list: Bookmark[] = [];
  for (const b of synced.sort((x, y) => x.createdAt.localeCompare(y.createdAt))) {
    if (!findBookmark(list, b.locator)) list.push(b);
  }
  return list.sort((x, y) => (x.locator.locations?.totalProgression ?? 0) - (y.locator.locations?.totalProgression ?? 0));
}

/** Envoie la différence entre deux listes de signets : les ajoutés, les supprimés. */
export function recordBookmarks(sync: SyncStore | undefined, productId: string | undefined, before: Bookmark[], after: Bookmark[]): void {
  if (!sync || !productId) return;
  const kept = new Set(after.map((b) => b.id));
  const had = new Set(before.map((b) => b.id));
  for (const b of after) {
    if (had.has(b.id)) continue;
    sync.put("bookmark", b.id, productId, { locator: b.locator, createdAt: b.createdAt });
    sync.event("bookmark_created", productId);
  }
  for (const b of before) {
    if (!kept.has(b.id)) sync.put("bookmark", b.id, productId, { locator: b.locator, createdAt: b.createdAt }, true);
  }
}

/** Ce que garde le serveur pour une annotation (format commun avec l'app) : le locator, la teinte, la note, les dates. */
const annotationData = (a: Annotation): Record<string, unknown> => ({
  locator: a.locator,
  color: a.color,
  ...(a.note ? { note: a.note } : {}),
  createdAt: a.createdAt,
  ...(a.updatedAt ? { updatedAt: a.updatedAt } : {}),
});

/** Les annotations du livre : celles de la synchro, plus les annotations locales jamais envoyées (envoyées à cette occasion). */
export function annotationsToOpen(sync: SyncStore | undefined, productId: string | undefined, local: Annotation[]): Annotation[] {
  if (!sync || !productId) return local;
  const synced = parseAnnotations(sync.list("annotation", productId).map((item) => ({ ...item.data, id: item.id })));
  const known = new Set(synced.map((a) => a.id));
  for (const annotation of local) {
    if (known.has(annotation.id) || sync.has("annotation", annotation.id)) continue;
    sync.put("annotation", annotation.id, productId, annotationData(annotation));
    synced.push(annotation);
  }
  return sortAnnotations(synced);
}

/** Envoie la différence entre deux listes d'annotations : les ajoutées, les modifiées (teinte, note), les supprimées. */
export function recordAnnotations(sync: SyncStore | undefined, productId: string | undefined, before: Annotation[], after: Annotation[]): void {
  if (!sync || !productId) return;
  const had = new Map(before.map((a) => [a.id, JSON.stringify(annotationData(a))]));
  const kept = new Set(after.map((a) => a.id));
  for (const a of after) {
    const data = annotationData(a);
    const previous = had.get(a.id);
    if (previous === JSON.stringify(data)) continue;
    sync.put("annotation", a.id, productId, data);
    if (previous === undefined) sync.event("annotation_created", productId, a.note ? { note: true } : {});
  }
  for (const a of before) {
    if (!kept.has(a.id)) sync.put("annotation", a.id, productId, annotationData(a), true);
  }
}

/** Les réglages de lecture du compte, par-dessus ceux de cet appareil ; `undefined` si le compte n'en a pas. */
export function preferencesToOpen(sync: SyncStore | undefined): Partial<ReadingPreferences> | undefined {
  const data = sync?.get("preferences", PREFERENCES_ID)?.data;
  if (!data) return undefined;
  // Seulement les réglages que ce lecteur connaît : un autre appareil peut en avoir d'autres.
  return Object.fromEntries(Object.entries(data).filter(([key]) => key in defaultReadingPreferences)) as Partial<ReadingPreferences>;
}

export function recordPreferences(sync: SyncStore | undefined, prefs: ReadingPreferences): void {
  sync?.putIfChanged("preferences", PREFERENCES_ID, null, prefs as unknown as Record<string, unknown>);
}

/** La progression (0 à 1) d'un livre d'après sa position synchronisée, pour la bibliothèque. */
export function syncedProgress(sync: SyncStore | undefined, productId: string): number | undefined {
  const locator = sync?.get("location", productId)?.data as LocatorJson | undefined;
  const value = locator?.locations?.totalProgression;
  return typeof value === "number" && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : undefined;
}
